import React, { useState, useEffect, useCallback } from 'react';
import CountryFlag from '../../../shared/components/CountryFlag';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Table, Button, Space, Tag, Badge, Modal, Form, Input, Select, message, Card, Drawer,
    Tooltip, InputNumber, Divider, Dropdown, Popconfirm, Popover, Progress, Typography,
    Grid, Result, Radio, Alert
} from 'antd';
import {
    PlusOutlined, PoweroffOutlined, PlayCircleOutlined, ReloadOutlined, DeleteOutlined,
    StopOutlined, CheckCircleOutlined, SwapOutlined, SyncOutlined, MoreOutlined,
    LoadingOutlined, CloseCircleOutlined, MinusCircleOutlined
} from '@ant-design/icons';
import { t } from '../../../shared/i18n';
import evmAdminService from '../../../shared/services/evmAdminService';
import MigrateModal from './MigrateModal';
import PageHeader from '../../../shared/components/PageHeader';
import { adminPath } from '../../../shared/config/paths';

const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const EVM_STATUS = {
    create: ['创建中', 'processing'],
    rebuild: ['重建中', 'processing'],
    active: ['运行中', 'success'],
    suspend: ['已暂停', 'error'],
    flow_limit: ['流量超限', 'warning'],
    migrate: ['迁移中', 'processing'],
    delete: ['删除中', 'default'],
    error: ['异常', 'error'],
};
// 节点上报的电源状态（null = 未知）
const POWER = {
    running: ['运行中', 'success'],
    shutoff: ['已关机', 'default'],
    paused: ['已挂起', 'warning'],
};
const powerKnown = (vm) => vm.power_state !== null && vm.power_state !== undefined && vm.power_state !== '';
// 某电源操作对该行是否可用：状态 active 且非 busy；电源状态已知时，开机仅非运行中、关机/重启仅运行中
const powerAllowed = (vm, action) => {
    if (vm.status !== 'active' || vm.busy) return false;
    if (!powerKnown(vm)) return true;
    return action === 'boot' ? vm.power_state !== 'running' : vm.power_state === 'running';
};
const powerBlockTip = (vm, action) => {
    if (vm.busy) return '任务执行中';
    if (vm.status !== 'active') return '当前状态不可操作';
    if (!powerAllowed(vm, action)) return action === 'boot' ? '已开机' : '未开机';
    return null;
};
const PowerBadge = ({ powerState }) => {
    if (powerState === null || powerState === undefined || powerState === '') return null;
    const [label, status] = POWER[powerState] || [String(powerState), 'default'];
    return <Badge status={status} text={<span style={{ fontSize: 12 }}>{label}</span>} />;
};

// 最近一次迁移：进行中显示进度，失败/有告警时显示一个小标签
const MigrationTag = ({ status, migration }) => {
    if (!migration) return null;
    const { status: ms, msg } = migration;
    if (status === 'migrate' && (ms === 2 || ms === 3)) return null; // 进度在状态标签的 tooltip 里
    if (ms === 4) return <Tooltip title={msg || '迁移失败'}><Tag color="error" style={{ marginInlineEnd: 0 }}>迁移失败</Tag></Tooltip>;
    if (ms === 5 && msg) return <Tooltip title={msg}><Tag color="warning" style={{ marginInlineEnd: 0 }}>已迁移</Tag></Tooltip>;
    return null;
};

const EvmStatusTag = ({ status, busy, powerState, migration }) => {
    let [label, color] = EVM_STATUS[status] || [status || '-', 'default'];
    const known = powerState !== null && powerState !== undefined && powerState !== '';
    if (status === 'active' && known) label = '正常';
    const tip = status === 'migrate' ? (migration?.msg || '排队中') : null;
    return (
        <Space size={4}>
            <Tooltip title={tip}><Tag color={color} style={{ marginInlineEnd: 0 }}>{label}</Tag></Tooltip>
            <MigrationTag status={status} migration={migration} />
            {known && <PowerBadge powerState={powerState} />}
            {busy && <Tooltip title="任务执行中"><LoadingOutlined style={{ fontSize: 12 }} /></Tooltip>}
        </Space>
    );
};

const fmtMb = (mb) => {
    const v = Number(mb) || 0;
    if (v >= 1024 * 1024) return `${(v / 1024 / 1024).toFixed(2)} TB`;
    if (v >= 1024) return `${(v / 1024).toFixed(1)} GB`;
    return `${v.toFixed(0)} MB`;
};

const specText = (vm) => {
    if (vm.plan) return typeof vm.plan === 'object' ? (vm.plan.name || '-') : vm.plan;
    const mem = Number(vm.memory) || 0;
    const memText = mem >= 1024 ? `${+(mem / 1024).toFixed(1)}G` : `${mem}M`;
    const disk = vm.disk_gb || vm.disk_size;
    return `${vm.core || '-'}核 ${memText}${disk ? ` ${disk}G` : ''}`;
};

const IpCell = ({ vm }) => {
    const list = Array.isArray(vm.ips) ? vm.ips.filter(Boolean) : [];
    const primary = vm.ip || list[0] || vm.main_ip || null;
    const rest = list.filter((x) => x !== primary);
    if (!primary) return <Text type="secondary">-</Text>;
    return (
        <Space size={4}>
            <Text copyable={{ text: primary }} style={{ fontFamily: 'monospace', fontSize: 13 }}>{primary}</Text>
            {rest.length > 0 && (
                <Popover trigger="click" content={
                    <Space direction="vertical" size={2}>
                        {rest.map((ip) => <Text key={ip} copyable style={{ fontFamily: 'monospace', fontSize: 13 }}>{ip}</Text>)}
                    </Space>
                }>
                    <Tag style={{ cursor: 'pointer', marginInlineEnd: 0 }}>+{rest.length}</Tag>
                </Popover>
            )}
        </Space>
    );
};

const TrafficCell = ({ traffic }) => {
    const used = Number(traffic?.used_mb) || 0;
    const limit = Number(traffic?.limit_mb) || 0;
    if (!limit) return <Text type="secondary" style={{ fontSize: 12 }}>{fmtMb(used)} / 不限</Text>;
    const pct = Math.min(100, (used / limit) * 100);
    return (
        <Tooltip title={`${fmtMb(used)} / ${fmtMb(limit)}`}>
            <div style={{ minWidth: 100 }}>
                <Progress percent={Number(pct.toFixed(1))} size="small" showInfo={false}
                    status={pct >= 100 ? 'exception' : 'normal'} style={{ margin: 0 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{fmtMb(used)} / {fmtMb(limit)}</Text>
            </div>
        </Tooltip>
    );
};

const BULK_ACTIONS = {
    boot: {
        label: '批量开机', run: (id) => evmAdminService.power(id, 'boot'),
        skipReason: (vm) => powerBlockTip(vm, 'boot'),
    },
    shutdown: {
        label: '批量关机', run: (id) => evmAdminService.power(id, 'shutdown'), danger: true,
        skipReason: (vm) => powerBlockTip(vm, 'shutdown'),
    },
    suspend: {
        label: '批量暂停', run: (id) => evmAdminService.suspendVm(id), danger: true,
        skipReason: (vm) => (vm.busy ? '任务执行中'
            : ['suspend', 'flow_limit'].includes(vm.status) ? '已暂停'
                : vm.status === 'delete' ? '当前状态不可操作' : null),
    },
    unsuspend: {
        label: '批量恢复', run: (id) => evmAdminService.unsuspendVm(id),
        skipReason: (vm) => (vm.busy ? '任务执行中'
            : !['suspend', 'flow_limit'].includes(vm.status) ? '未暂停' : null),
    },
};

const VmsManagement = () => {
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [modal, modalCtx] = Modal.useModal();

    const [vms, setVms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [migrateModalVisible, setMigrateModalVisible] = useState(false);
    const [currentVm, setCurrentVm] = useState(null);

    // 筛选
    const [searchParams] = useSearchParams();
    const [keywordInput, setKeywordInput] = useState(() => searchParams.get('search') || '');
    const [loadError, setLoadError] = useState('');
    const [filters, setFilters] = useState(() => ({ keyword: (searchParams.get('search') || '').trim(), status: undefined, node_id: undefined, zone_id: undefined, uid: undefined, kind: undefined }));
    const [filterNodes, setFilterNodes] = useState([]);
    const [zones, setZones] = useState([]);

    // 选择 / 批量
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkRunning, setBulkRunning] = useState(false);
    const [acting, setActing] = useState({});

    // 辅助数据（创建 / 迁移）
    const [areas, setAreas] = useState([]);
    const [nodes, setNodes] = useState([]);        // area-scoped, for the create form
    // eslint-disable-next-line no-unused-vars
    const [allNodes, setAllNodes] = useState([]);  // full list (migrate targets now come from preflight)
    const [images, setImages] = useState([]);
    const [users, setUsers] = useState([]);
    const [userSgs, setUserSgs] = useState([]);
    const [userVpcs, setUserVpcs] = useState([]); // target user's VPCs in the chosen node's zone
    // eslint-disable-next-line no-unused-vars
    const [ipPools, setIpPools] = useState([]);

    const [createForm] = Form.useForm();

    // Helper to extract array from paginated response
    const extractArray = (data, ...keys) => {
        if (Array.isArray(data)) return data;
        for (const key of keys) {
            if (Array.isArray(data?.[key])) return data[key];
        }
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    };

    const loadVms = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') params[k] = v;
            });
            const response = await evmAdminService.getVms(params);
            if (response.status === 'success') {
                setLoadError('');
                const list = extractArray(response.data, 'vms');
                setVms(list);
                setSelectedRowKeys((keys) => keys.filter((k) => list.some((vm) => vm.id === k)));
            } else {
                setLoadError(response.msg || response.error || '加载失败');
            }
        } catch (error) {
            setLoadError('加载失败');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { loadVms(); }, [loadVms]);

    // 迁移进行中时定时刷新进度
    const migrating = vms.some((vm) => vm.status === 'migrate');
    useEffect(() => {
        if (!migrating) return undefined;
        const t = setInterval(loadVms, 15000);
        return () => clearInterval(t);
    }, [migrating, loadVms]);

    useEffect(() => {
        loadAuxiliaryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAuxiliaryData = async () => {
        try {
            const [areasRes, allNodesRes, imagesRes, usersRes, ipPoolsRes, nodeListRes, zoneRes] = await Promise.all([
                evmAdminService.getAreas(),
                evmAdminService.getNodes(),
                evmAdminService.getImages(),
                evmAdminService.getUsers(),
                evmAdminService.getIpPools(),
                evmAdminService.getNodeList(),
                evmAdminService.getZoneList(),
            ]);

            if (areasRes.status === 'success') setAreas(extractArray(areasRes.data, 'areas'));
            if (allNodesRes.status === 'success') setAllNodes(extractArray(allNodesRes.data, 'nodes'));
            if (imagesRes.status === 'success') setImages(extractArray(imagesRes.data, 'images'));
            if (usersRes.status === 'success') setUsers(extractArray(usersRes.data, 'users'));
            if (ipPoolsRes.status === 'success') setIpPools(extractArray(ipPoolsRes.data, 'pools'));
            if (nodeListRes?.status === 'success') setFilterNodes(extractArray(nodeListRes.data, 'nodes'));
            if (zoneRes?.status === 'success') setZones(extractArray(zoneRes.data, 'zones'));
        } catch (error) {
            console.error('加载辅助数据失败', error);
        }
    };

    // Nodes are scoped to the selected area — the backend requires node_id to
    // belong to area_id, so reload the node list whenever the area changes and
    // clear the previously chosen node/pool.
    const loadNodesByArea = async (areaId) => {
        createForm.setFieldsValue({ node_id: undefined, subnet_id: undefined });
        setUserVpcs([]);
        if (!areaId) { setNodes([]); return; }
        try {
            const res = await evmAdminService.getNodes(areaId);
            if (res.status === 'success') setNodes(extractArray(res.data, 'nodes'));
        } catch (error) {
            console.error('加载节点失败', error);
        }
    };

    const genPassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
        let out = '';
        const arr = new Uint32Array(14);
        (window.crypto || window.msCrypto).getRandomValues(arr);
        for (let i = 0; i < arr.length; i++) out += chars[arr[i] % chars.length];
        createForm.setFieldsValue({ password: out });
    };

    // 单条操作：统一 loading + 后端 msg 回显
    const runAction = async (vm, key, label, fn, delay = 0) => {
        setActing((s) => ({ ...s, [vm.id]: key }));
        try {
            const response = await fn();
            if (response?.status === 'success') {
                message.success(response.msg || `${label}已提交`);
                if (delay) setTimeout(loadVms, delay); else loadVms();
                return true;
            }
            message.error(response?.msg || response?.error || `${label}失败`);
        } catch (error) {
            message.error(`${label}失败`);
        } finally {
            setActing((s) => { const n = { ...s }; delete n[vm.id]; return n; });
        }
        return false;
    };

    const handlePowerAction = (vm, action) => {
        const names = { boot: '开机', shutdown: '关机', reboot: '重启' };
        return runAction(vm, action, names[action], () => evmAdminService.power(vm.id, action), 2000);
    };

    const confirmThen = (title, onOk, danger = true, content) => {
        modal.confirm({
            title,
            content,
            okText: '确定',
            cancelText: '取消',
            okButtonProps: danger ? { danger: true } : undefined,
            onOk,
        });
    };

    const vmLabel = (vm) => vm.name || vm.hostname || `#${vm.id}`;

    const handleMenu = (vm, key) => {
        switch (key) {
            case 'suspend':
                confirmThen(`暂停 ${vmLabel(vm)}？`, () => runAction(vm, 'suspend', '暂停', () => evmAdminService.suspendVm(vm.id)));
                break;
            case 'unsuspend':
                confirmThen(`恢复 ${vmLabel(vm)}？`, () => runAction(vm, 'unsuspend', '恢复', () => evmAdminService.unsuspendVm(vm.id)), false);
                break;
            case 'migrate':
                setCurrentVm(vm);
                setMigrateModalVisible(true);
                break;
            case 'reset_flow':
                confirmThen(`重置 ${vmLabel(vm)} 的流量计数？`, () => runAction(vm, 'reset_flow', '流量重置', () => evmAdminService.resetFlow(vm.id)));
                break;
            case 'delete':
                confirmThen(`删除 ${vmLabel(vm)}？`, () => runAction(vm, 'delete', '删除', () => evmAdminService.deleteVm(vm.id)), true, '实例及其磁盘将被销毁，不可恢复。');
                break;
            default:
        }
    };

    // 批量：顺序执行，结束后弹出逐条结果
    const runBulk = (key) => {
        const action = BULK_ACTIONS[key];
        const selected = vms.filter((vm) => selectedRowKeys.includes(vm.id));
        if (!selected.length) return;
        const skipped = [];
        const targets = [];
        selected.forEach((vm) => {
            const reason = action.skipReason(vm);
            if (reason) skipped.push({ vm, skipped: true, ok: false, msg: reason });
            else targets.push(vm);
        });
        if (!targets.length) {
            message.warning(`所选 ${selected.length} 台均不满足条件`);
            return;
        }
        confirmThen(`${action.label}（${targets.length} 台${skipped.length ? `，跳过 ${skipped.length} 台` : ''}）？`, async () => {
            setBulkRunning(true);
            const results = [];
            for (const vm of targets) {
                try {
                    const res = await action.run(vm.id);
                    results.push({ vm, ok: res?.status === 'success', msg: res?.msg || res?.error || '' });
                } catch (e) {
                    results.push({ vm, ok: false, msg: e?.message || '请求失败' });
                }
            }
            setBulkRunning(false);
            const okCount = results.filter((r) => r.ok).length;
            results.push(...skipped);
            modal.info({
                title: `${action.label}：成功 ${okCount} / ${targets.length}${skipped.length ? `，跳过 ${skipped.length}` : ''}`,
                width: 520,
                content: (
                    <div style={{ maxHeight: 360, overflowY: 'auto', marginTop: 8 }}>
                        {results.map((r) => (
                            <div key={r.vm.id} style={{ display: 'flex', gap: 8, padding: '2px 0' }}>
                                {r.skipped
                                    ? <MinusCircleOutlined style={{ color: 'var(--ant-color-text-quaternary, #bfbfbf)', marginTop: 4 }} />
                                    : r.ok
                                        ? <CheckCircleOutlined style={{ color: 'var(--ant-color-success, #52c41a)', marginTop: 4 }} />
                                        : <CloseCircleOutlined style={{ color: 'var(--ant-color-error, #ff4d4f)', marginTop: 4 }} />}
                                <span style={{ minWidth: 0 }}>
                                    {vmLabel(r.vm)}
                                    {r.msg && <Text type={r.ok || r.skipped ? 'secondary' : 'danger'} style={{ marginLeft: 8, fontSize: 12 }}>{r.msg}</Text>}
                                </span>
                            </div>
                        ))}
                    </div>
                ),
            });
            setTimeout(loadVms, 1500);
        }, BULK_ACTIONS[key].danger);
    };

    // Target user's VPCs in the selected node's zone (admin VPC API, user_id filter).
    const loadUserVpcs = async (uid, nodeId) => {
        createForm.setFieldsValue({ subnet_id: undefined });
        setUserVpcs([]);
        const node = nodes.find((n) => n.id === nodeId);
        if (!uid || !node) return;
        const r = await evmAdminService.getVpcList({ user_id: uid, zone_id: node.zone_id || undefined, area_id: node.area_id });
        if (r?.status === 'success') setUserVpcs(extractArray(r.data).filter((v) => v.status === 'active'));
    };

    const loadUserSgs = async (uid) => {
        createForm.setFieldValue('security_group_ids', []);
        setUserSgs([]);
        if (!uid) return;
        const r = await evmAdminService.getSecurityGroupList({ user_id: uid });
        if (r?.status === 'success') setUserSgs(extractArray(r.data));
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setUserSgs([]);
        setUserVpcs([]);
        setNodes([]); // area-scoped node list; cleared until an area is picked
        setCreateDrawerVisible(true);
    };

    const handleCreate = async (input) => {
        // Network choice → VmManager input (VPC only = network_mode vpc; else pool IPs + optional VPC NIC).
        const { public_ipv4: v4 = 1, vpc_mode: vpcMode = 'none', new_vpc_name: vpcName, new_vpc_cidr: vpcCidr, ...values } = input;
        if (!v4) Object.assign(values, { network_mode: 'vpc', public_ip_num: 0, private_ip_num: 0 });
        if (vpcMode !== 'existing') delete values.subnet_id;
        if (vpcMode === 'new') values.new_vpc = { name: vpcName || `vpc-${Date.now() % 100000}`, cidr: vpcCidr };
        try {
            const response = await evmAdminService.createVm(values);
            if (response.status === 'success') {
                message.success(response.msg || 'VM创建任务已提交');
                setCreateDrawerVisible(false);
                loadVms();
            } else {
                message.error(response.msg || 'VM创建失败');
            }
        } catch (error) {
            message.error('VM创建失败');
        }
    };

    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
    const hasFilters = Object.values(filters).some((v) => v !== undefined && v !== null && v !== '');

    const renderActions = (vm) => {
        const busyAction = acting[vm.id];
        const can = (a) => powerAllowed(vm, a) && !busyAction;
        const tip = (a) => powerBlockTip(vm, a);
        const isSuspended = vm.status === 'suspend' || vm.status === 'flow_limit';
        const menuItems = [
            isSuspended
                ? { key: 'unsuspend', icon: <CheckCircleOutlined />, label: '恢复', disabled: vm.busy }
                : { key: 'suspend', icon: <StopOutlined />, label: '暂停', disabled: vm.busy || vm.status === 'delete' },
            vm.status === 'migrate'
                ? { key: 'migrate', icon: <SwapOutlined />, label: '迁移进度' }
                : { key: 'migrate', icon: <SwapOutlined />, label: '迁移', disabled: vm.busy || vm.status !== 'active' },
            { key: 'reset_flow', icon: <SyncOutlined />, label: '重置流量' },
            { type: 'divider' },
            { key: 'delete', icon: <DeleteOutlined />, label: '删除', danger: true, disabled: vm.busy || vm.status === 'delete' },
        ];
        return (
            <Space size={2}>
                <Tooltip title={tip('boot') || '开机'}>
                    <Button size="small" type="text" icon={<PlayCircleOutlined />}
                        disabled={!can('boot')} loading={busyAction === 'boot'}
                        onClick={() => handlePowerAction(vm, 'boot')} />
                </Tooltip>
                <Popconfirm title="确认关机该客户实例？" okText="关机" okType="danger" cancelText="取消"
                    disabled={!can('shutdown')} onConfirm={() => handlePowerAction(vm, 'shutdown')}>
                    <Tooltip title={tip('shutdown') || '关机'}>
                        <Button size="small" type="text" icon={<PoweroffOutlined />}
                            disabled={!can('shutdown')} loading={busyAction === 'shutdown'} />
                    </Tooltip>
                </Popconfirm>
                <Popconfirm title="确认重启该客户实例？" okText="重启" cancelText="取消"
                    disabled={!can('reboot')} onConfirm={() => handlePowerAction(vm, 'reboot')}>
                    <Tooltip title={tip('reboot') || '重启'}>
                        <Button size="small" type="text" icon={<ReloadOutlined />}
                            disabled={!can('reboot')} loading={busyAction === 'reboot'} />
                    </Tooltip>
                </Popconfirm>
                <Dropdown trigger={['click']} menu={{ items: menuItems, onClick: ({ key }) => handleMenu(vm, key) }}>
                    <Button size="small" type="text" icon={<MoreOutlined />}
                        loading={['suspend', 'unsuspend', 'migrate', 'reset_flow', 'delete'].includes(busyAction)} />
                </Dropdown>
            </Space>
        );
    };

    const fmtTime = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '-');

    const columns = [
        {
            title: '实例',
            key: 'name',
            fixed: isMobile ? undefined : 'left',
            render: (_, vm) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => navigate(adminPath(`/evm/vms/${vm.id}`))} style={{ fontWeight: 500 }}>
                        {vm.name || `#${vm.id}`}
                        {vm.kind === 'vds' && <Tag color="purple" style={{ marginInlineStart: 6 }}>VDS</Tag>}
                    </a>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        #{vm.id}{vm.hostname && vm.hostname !== vm.name ? ` · ${vm.hostname}` : ''}
                    </Text>
                </Space>
            ),
        },
        { title: '状态', key: 'status', render: (_, vm) => <EvmStatusTag status={vm.status} busy={vm.busy} powerState={vm.power_state} migration={vm.migration} /> },
        { title: 'IP', key: 'ip', render: (_, vm) => <IpCell vm={vm} /> },
        {
            title: '所有者',
            key: 'owner',
            render: (_, vm) => {
                const o = vm.owner || {};
                const name = o.username || vm.username;
                const uid = o.id ?? vm.uid;
                return (
                    <Space direction="vertical" size={0}>
                        <a onClick={() => setFilter('uid', uid)}>{name || `#${uid}`}</a>
                        {o.email && <Text type="secondary" style={{ fontSize: 12 }}>{o.email}</Text>}
                    </Space>
                );
            },
        },
        { title: '节点', key: 'node', render: (_, vm) => vm.node?.name || vm.node_name || (vm.node_id ? `#${vm.node_id}` : '-') },
        { title: '地域', dataIndex: 'region', key: 'region', render: (v, vm) => (v ? <><CountryFlag code={areas.find((a) => Number(a.id) === Number(zones.find((z) => Number(z.id) === Number(vm.zone_id))?.area_id))?.country} />{v}</> : '-') },
        { title: '配置', key: 'spec', render: (_, vm) => specText(vm) },
        { title: '流量', key: 'traffic', width: 140, render: (_, vm) => <TrafficCell traffic={vm.traffic} /> },
        { title: '到期', dataIndex: 'expires_at', key: 'expires_at', render: (v) => (v ? String(v).slice(0, 10) : '-') },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
            render: fmtTime,
        },
        { title: '操作', key: 'action', fixed: 'right', width: 150, render: (_, vm) => renderActions(vm) },
    ];

    return (
        <div>
            {modalCtx}
            <PageHeader
                title="云服务器实例"
                extra={
                    <>
                        <Button icon={<SyncOutlined />} onClick={loadVms} loading={loading} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>创建实例</Button>
                    </>
                }
            />

            <Card size="small" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <Input.Search
                        allowClear
                        placeholder="名称 / 主机名 / IP / 用户"
                        value={keywordInput}
                        onChange={(e) => {
                            setKeywordInput(e.target.value);
                            if (!e.target.value) setFilter('keyword', '');
                        }}
                        onSearch={(v) => setFilter('keyword', v.trim())}
                        style={{ width: isMobile ? '100%' : 280 }}
                    />
                    <Select allowClear placeholder="状态" value={filters.status} onChange={(v) => setFilter('status', v)}
                        style={{ width: isMobile ? 'calc(50% - 4px)' : 120 }}
                        options={Object.entries(EVM_STATUS).map(([k, [l]]) => ({ value: k, label: l }))} />
                    <Select allowClear showSearch optionFilterProp="label" placeholder="节点" value={filters.node_id}
                        onChange={(v) => setFilter('node_id', v)} style={{ width: isMobile ? 'calc(50% - 4px)' : 160 }}
                        options={filterNodes.map((n) => ({ value: n.id, label: n.name }))} />
                    <Select allowClear placeholder="地域" value={filters.zone_id} onChange={(v) => setFilter('zone_id', v)}
                        style={{ width: isMobile ? 'calc(50% - 4px)' : 160 }}
                        options={zones.map((z) => ({ value: z.id, label: <><CountryFlag code={areas.find((a) => Number(a.id) === Number(z.area_id))?.country} />{z.area_name ? `${z.area_name} · ${z.name}` : z.name}</> }))} />
                    <Select allowClear placeholder={t('Type')} value={filters.kind} onChange={(v) => setFilter('kind', v)}
                        style={{ width: isMobile ? 'calc(50% - 4px)' : 110 }}
                        options={[{ value: 'cloud', label: t('Cloud servers') }, { value: 'vds', label: 'VDS' }]} />
                    <InputNumber placeholder="用户 UID" min={1} value={filters.uid} onChange={(v) => setFilter('uid', v ?? undefined)}
                        style={{ width: isMobile ? 'calc(50% - 4px)' : 120 }} />
                    {hasFilters && (
                        <Button type="link" onClick={() => {
                            setKeywordInput('');
                            setFilters({ keyword: '', status: undefined, node_id: undefined, zone_id: undefined, uid: undefined, kind: undefined });
                        }}>清除</Button>
                    )}
                </div>
            </Card>

            {selectedRowKeys.length > 0 && (
                <Card size="small" style={{ marginBottom: 12 }}>
                    <Space wrap>
                        <Text>已选 {selectedRowKeys.length} 台</Text>
                        <Button size="small" icon={<PlayCircleOutlined />} loading={bulkRunning} onClick={() => runBulk('boot')}>批量开机</Button>
                        <Button size="small" icon={<PoweroffOutlined />} loading={bulkRunning} onClick={() => runBulk('shutdown')}>批量关机</Button>
                        <Button size="small" icon={<StopOutlined />} loading={bulkRunning} onClick={() => runBulk('suspend')}>批量暂停</Button>
                        <Button size="small" icon={<CheckCircleOutlined />} loading={bulkRunning} onClick={() => runBulk('unsuspend')}>批量恢复</Button>
                        <Button size="small" type="link" disabled={bulkRunning} onClick={() => setSelectedRowKeys([])}>取消选择</Button>
                    </Space>
                </Card>
            )}

            {loadError && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }} message={loadError}
                    action={<Button size="small" onClick={loadVms}>重试</Button>} />
            )}

            <Card styles={{ body: { padding: 0 } }}>
                <Table
                    sticky
                    columns={columns}
                    dataSource={vms}
                    loading={loading}
                    rowKey="id"
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys, preserveSelectedRowKeys: false }}
                    locale={{ emptyText: <Result status="info" icon={null} subTitle={hasFilters ? '无匹配实例' : '暂无实例'} /> }}
                    pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (n) => (n >= 500 ? `仅显示最新 ${n} 台，请用筛选缩小范围` : `共 ${n} 台`), size: isMobile ? 'small' : 'default' }}
                />
            </Card>

            {/* 创建VM抽屉 */}
            <Drawer
                title="创建虚拟机"
                width={isMobile ? '100%' : 600}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>取消</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>
                            创建
                        </Button>
                    </Space>
                }
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        name="uid"
                        label="用户"
                        rules={[{ required: true, message: '请选择用户' }]}
                    >
                        <Select
                            showSearch
                            placeholder="选择用户"
                            onChange={(uid) => { loadUserSgs(uid); loadUserVpcs(uid, createForm.getFieldValue('node_id')); }}
                            filterOption={(input, option) =>
                                String(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {`${user.username} (ID: ${user.id})`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="area_id"
                        label="地区"
                        rules={[{ required: true, message: '请选择地区' }]}
                    >
                        <Select placeholder="选择地区" onChange={loadNodesByArea}>
                            {areas.map(area => (
                                <Option key={area.id} value={area.id}>
                                    <CountryFlag code={area.country} />{area.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="node_id"
                        label="节点"
                        rules={[{ required: true, message: '请选择节点' }]}
                    >
                        <Select placeholder="请先选择地区" disabled={!nodes.length} onChange={(id) => loadUserVpcs(createForm.getFieldValue('uid'), id)}>
                            {nodes.map(node => (
                                <Option key={node.id} value={node.id}>
                                    {node.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="image_id"
                        label="镜像"
                        rules={[{ required: true, message: '请选择镜像' }]}
                    >
                        <Select placeholder="选择镜像" showSearch optionFilterProp="children">
                            {images.map(image => (
                                <Option key={image.id} value={image.id}>
                                    {image.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="security_group_ids" label="安全组">
                        <Select
                            mode="multiple"
                            allowClear
                            maxCount={5}
                            placeholder="默认安全组"
                            optionFilterProp="label"
                            options={userSgs.map((g) => ({ value: g.id, label: g.name }))}
                        />
                    </Form.Item>

                    <Form.Item name="hostname" label="主机名" extra="留空则自动生成">
                        <Input placeholder="EVM-xxxxxxxx" />
                    </Form.Item>

                    <Form.Item name="password" label="登录密码" extra="留空则自动生成">
                        <Input.Password
                            placeholder="留空自动生成"
                            addonAfter={<a onClick={genPassword}>生成</a>}
                        />
                    </Form.Item>

                    <Divider>资源配置</Divider>

                    <Form.Item
                        name="core"
                        label="CPU核心数"
                        rules={[{ required: true, message: '请输入CPU核心数' }]}
                        initialValue={1}
                    >
                        <InputNumber min={1} max={64} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="memory"
                        label="内存 (MB)"
                        rules={[{ required: true, message: '请输入内存大小' }]}
                        initialValue={1024}
                    >
                        <InputNumber min={512} max={524288} step={512} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="sys_disk_size"
                        label="系统盘 (GB)"
                        extra="留空则使用镜像默认大小"
                    >
                        <InputNumber min={0} max={2000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="data_disk_size"
                        label="数据盘 (GB)"
                        extra="0 表示不挂数据盘"
                        initialValue={0}
                    >
                        <InputNumber min={0} max={2000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Divider>网络配置</Divider>

                    <Form.Item name="vpc_mode" label="VPC" initialValue="none">
                        <Radio.Group
                            optionType="button"
                            onChange={(e) => { if (e.target.value === 'none') createForm.setFieldsValue({ public_ipv4: 1 }); }}
                            options={[
                                { value: 'none', label: '不加入' },
                                { value: 'existing', label: '选已有', disabled: !userVpcs.length },
                                { value: 'new', label: '新建' },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(a, b) => a.vpc_mode !== b.vpc_mode}>
                        {({ getFieldValue }) => (getFieldValue('vpc_mode') === 'existing' ? (
                            <Form.Item name="subnet_id" label="子网" rules={[{ required: true, message: '请选择子网' }]}>
                                <Select
                                    placeholder="用户在该可用区的 VPC"
                                    options={userVpcs.map((v) => ({
                                        label: `${v.name} (${v.cidr})`,
                                        options: (v.subnets || []).filter((sn) => sn.status === 'active').map((sn) => ({ value: sn.id, label: `${v.name} · ${sn.cidr}` })),
                                    }))}
                                />
                            </Form.Item>
                        ) : getFieldValue('vpc_mode') === 'new' ? (
                            <Space.Compact style={{ width: '100%', marginBottom: 24 }}>
                                <Form.Item name="new_vpc_name" noStyle><Input placeholder="VPC 名称" maxLength={100} /></Form.Item>
                                <Form.Item name="new_vpc_cidr" noStyle initialValue="10.1.0.0/16" rules={[{ required: true }]}>
                                    <Input addonBefore="网段" />
                                </Form.Item>
                            </Space.Compact>
                        ) : null)}
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(a, b) => a.vpc_mode !== b.vpc_mode || a.public_ipv4 !== b.public_ipv4}>
                        {({ getFieldValue }) => (
                            <>
                                <Form.Item name="public_ipv4" label="公网 IPv4" initialValue={1}>
                                    <Radio.Group
                                        optionType="button"
                                        options={[
                                            { value: 1, label: '分配' },
                                            { value: 0, label: <Tooltip title={getFieldValue('vpc_mode') === 'none' ? '需先加入 VPC' : null}>不分配</Tooltip>, disabled: getFieldValue('vpc_mode') === 'none' },
                                        ]}
                                    />
                                </Form.Item>
                                {getFieldValue('public_ipv4') !== 0 && (
                                    <>
                    <Form.Item
                        name="public_ip_num"
                        label="公网 IP 数量"
                        initialValue={1}
                        extra="从该节点绑定的公网 IP 池自动分配"
                    >
                        <InputNumber min={0} max={16} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="private_ip_num"
                        label="内网 IP 数量"
                        initialValue={0}
                    >
                        <InputNumber min={0} max={16} style={{ width: '100%' }} />
                    </Form.Item>
                                    </>
                                )}
                            </>
                        )}
                    </Form.Item>

                    <Form.Item
                        name="net_out"
                        label="出站带宽 (Mbps)"
                        initialValue={100}
                    >
                        <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="net_in"
                        label="入站带宽 (Mbps)"
                        initialValue={100}
                    >
                        <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Drawer>

            <MigrateModal
                vm={currentVm}
                open={migrateModalVisible}
                onClose={() => setMigrateModalVisible(false)}
                onChanged={loadVms}
            />
        </div>
    );
};

export default VmsManagement;
